<template>
    <div id="app-wrap" :class="loadedInit ? '' : 'app-hide'" >
        <div>
            <div v-if="showAllNodesMenu" class="public-nodes-menu-wrap">
                <div class="public-nodes-menu">
                    <div v-for="(next, i) in $store.state.allNodesFiltered" :key="i">
                        <a @click.prevent="handleNode(next)">{{ next.TypesString || 'Node' }}: <b>{{ next.Name }}</b></a>
                    </div>
                </div>
            </div>
        </div>
        <div v-if="$store.state.signedIn" class="top-search-wrap">
            <div>
                <router-link to="/playbook/home" class="top-logos">
                    <img src="https://superform-portal.nyc3.digitaloceanspaces.com/playbook_logos/logo-light.png" />
                    <img v-if="$store.state.user.logoPath" :src="$store.state.spacesUrlPublic + $store.state.user.logoPath" />
                </router-link>
            </div>
            <div>
                <top-search />
                <user-switch />
            </div>
            <div class="top-right">
                <!-- Opens the floating timer; while it's closed and counting, shows the time left -->
                <button type="button" class="top-timer-btn" :class="[ $store.state.timer.status, $store.state.timer.open ? 'open' : '', $store.state.timer.label && !$store.state.timer.open ? 'counting' : '' ]" title="Timer" aria-label="Timer" @click="$store.state.timer.open = !$store.state.timer.open">
                    <svg viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="13" r="8" /><path d="M12 9v4l2.5 2.5M10 2h4M12 2v3" /></svg>
                    <span v-if="$store.state.timer.label && !$store.state.timer.open">{{ $store.state.timer.label }}</span>
                </button>
                <!-- Quick add (from 1300px, and again at 991px and down; in between, they're in the user menu). Both need an employee record. -->
                <div v-if="$store.state.user.employeeID" class="top-quick-add">
                    <button type="button" class="btn top-quick-btn" title="Add a task to your task manager" aria-label="Add task" @click="$store.state.quickAdd = 'task'"><svg class="top-quick-icon" viewBox="0 0 24 24" aria-hidden="true"><rect x="3" y="3" width="18" height="18" rx="3" /><path d="M8 12.5l3 3 5-6" /></svg><span>Task</span></button>
                    <button type="button" class="btn-light top-quick-btn" title="Add an agenda item to a meeting" aria-label="Add agenda item" @click="$store.state.quickAdd = 'agenda'"><svg class="top-quick-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M9 6h11M9 12h11M9 18h11" /><circle cx="4.5" cy="6" r="1" /><circle cx="4.5" cy="12" r="1" /><circle cx="4.5" cy="18" r="1" /></svg><span>Agenda</span></button>
                    <button v-if="$store.state.numbersToEnter" type="button" class="btn-light top-quick-btn" :title="'Enter your scorecard numbers (' + $store.state.numbersToEnter + ' to enter)'" aria-label="Enter scorecard numbers" @click="$store.state.quickAdd = 'numbers'"><svg class="top-quick-icon" viewBox="0 0 24 24" aria-hidden="true"><path d="M3 20h18M6 20v-6M12 20V5M18 20v-9" /></svg><span>Numbers</span></button>
                </div>
                <router-link v-if="canForge" to="/ai-hub" class="ai-btn top-forge-btn" title="The Forge"><svg aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="15" height="15" viewBox="0 0 24 24" fill="currentColor" fill-rule="evenodd"><path d="M12 23c-4.4 0-8-3.1-8-7.5 0-3.3 2-5.6 3.6-7.4.4-.4 1.1-.1 1.1.5 0 1.3.6 2.6 1.6 3.2C10.1 7.7 11.6 4 15 1.2c.4-.3 1 0 1 .5-.1 2.6.9 4.5 2.2 6.4C19.3 9.8 20 11.6 20 15.5 20 19.9 16.4 23 12 23zm0-2c1.9 0 3.3-1.3 3.3-3.1 0-1.6-.9-2.7-2.1-3.9-.3-.3-.8-.1-.8.3-.1 1-.6 1.8-1.3 2.2-.1-1.1-.5-2-1.1-2.6-.3-.3-.8-.1-.8.3-.3 1.3-1.5 2.2-1.5 3.7C7.7 19.7 9.8 21 12 21z"/></svg><span>The Forge</span></router-link>
                <user-sub-menu />
            </div>
        </div>
        <floating-timer v-if="$store.state.signedIn" />
        <!-- Ask the Forge: every page, every login, every company (keyed so a company switch starts a fresh conversation) -->
        <ask-forge v-if="loadedInit && $store.state.signedIn && !isEmbedded" :key="$store.state.user.companyID" />
        <quick-add-task v-if="$store.state.quickAdd == 'task'" @close="$store.state.quickAdd = null" />
        <quick-add-agenda-item v-if="$store.state.quickAdd == 'agenda'" @close="$store.state.quickAdd = null" />
        <my-numbers v-if="$store.state.quickAdd == 'numbers'" popup @close="$store.state.quickAdd = null" />
        <div v-if="$store.state.quickAddToast" class="quick-add-toast" role="status">
            <span>{{ $store.state.quickAddToast.text }}</span>
            <a v-if="$store.state.quickAddToast.link" @click.prevent="openQuickAddLink()">{{ $store.state.quickAddToast.link.label }}</a>
            <button type="button" class="quick-add-toast-x" aria-label="Dismiss" @click="$store.state.quickAddToast = null"><svg viewBox="0 0 24 24"><path d="M6 6l12 12M18 6L6 18" /></svg></button>
        </div>
        <div id="app-main">
            <div v-if="$store.state.signedIn" class="mobile-nav-show-hide"><span @click="toggleMenu()"><svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M24 6h-24v-4h24v4zm0 4h-24v4h24v-4zm0 8h-24v4h24v-4z"/></svg></span></div>
            <nav v-if="$store.state.signedIn" :class="showMenu ? 'show-nav' : 'hide-nav'">
                <!--
                <div class="top-bar-imgs">
                    <img v-if="$store.state.user.lightLogoPath && showMenu" :src="$store.state.spacesUrlPublic + $store.state.user.lightLogoPath" />
                    <div v-else style="height:30px"></div>
                </div>
                -->
                <div v-if="!showMenu" style="height:40px;min-height:40px"></div>
                <div class="nav-show-hide"><span v-html='showMenu ? `<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-left" class="svg-inline--fa fa-chevron-left fa-w-10" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"></path></svg><svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-left" class="svg-inline--fa fa-chevron-left fa-w-10" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M34.52 239.03L228.87 44.69c9.37-9.37 24.57-9.37 33.94 0l22.67 22.67c9.36 9.36 9.37 24.52.04 33.9L131.49 256l154.02 154.75c9.34 9.38 9.32 24.54-.04 33.9l-22.67 22.67c-9.37 9.37-24.57 9.37-33.94 0L34.52 272.97c-9.37-9.37-9.37-24.57 0-33.94z"></path></svg>` : `<svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-right" class="svg-inline--fa fa-chevron-right fa-w-10" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path></svg><svg aria-hidden="true" focusable="false" data-prefix="fas" data-icon="chevron-right" class="svg-inline--fa fa-chevron-right fa-w-10" role="img" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 320 512"><path fill="currentColor" d="M285.476 272.971L91.132 467.314c-9.373 9.373-24.569 9.373-33.941 0l-22.667-22.667c-9.357-9.357-9.375-24.522-.04-33.901L188.505 256 34.484 101.255c-9.335-9.379-9.317-24.544.04-33.901l22.667-22.667c9.373-9.373 24.569-9.373 33.941 0L285.475 239.03c9.373 9.372 9.373 24.568.001 33.941z"></path></svg>`' @click="toggleMenu()"></span></div>
                <div class="nav-main">
                    <div v-if="showMenu && $store.state.user.allCompanies && $store.state.user.allCompanies.length > 1" style="margin-top:10px;padding-right:40px;">
                        <select v-model="$store.state.user.companyID" @change="switchCompany()">
                            <option v-for="(c, i) in $store.state.user.allCompanies" :key="i" :value="c.companyID">{{ c.name }}</option>
                        </select>
                    </div>
                    <div>
                        <!-- Sections are built from navSections (script); favorites are listed where its { favorites: true } marker sits -->
                        <template v-for="section in navSections" :key="section.name">
                            <div class="menu-head" @click="() => { $store.state.adminSelectedMenu = section.name }">{{ section.name }}</div>
                            <div v-if="$store.state.adminSelectedMenu == section.name">
                                <template v-for="(entry, i) in section.entries" :key="(entry.key || entry.subhead || 'favorites') + '-' + i">
                                    <template v-if="entry.favorites">
                                        <nav-item v-for="item in favoriteItems" :key="'fav-' + item.key" :item="item" :favorite="true" :show-star="showMenu" @toggle-favorite="toggleFavorite" />
                                    </template>
                                    <div v-else-if="entry.subhead" class="menu-subhead">{{ entry.subhead }}</div>
                                    <nav-item v-else :item="entry" :favorite="$store.state.navFavorites.includes(entry.key)" :show-star="showMenu" @toggle-favorite="toggleFavorite" />
                                </template>
                            </div>
                        </template>

                        <div class="mobile-991" style="margin-top:25px">
                            <user-switch v-if="$store.state.user.userToPublic" style="margin-bottom:15px;" />
                            <a v-if="!$store.state.user.returnAdminID && !$store.state.user.supportSession" @click.prevent="() => { $store.state.showChangePassword = true; }">Change Password</a>
                            <a @click.prevent="logOut()">Log Out</a>
                        </div>

                    </div>
                </div>
            </nav>
            <div class="content" id="main-content">
                <div v-if="!$store.state.routeLoaded" id="lds-wrapper"><div id="lds" class="lds-ring"><div></div><div></div><div></div><div></div></div></div>
                <router-view v-if="loadedInit" v-show="$store.state.routeLoaded" :key="$route.path" />
            </div>
        </div>

        <!-- Tasks someone sent you, until you finish or decline them -->
        <task-reminders v-if="loadedInit && $store.state.signedIn && $store.state.user.employeeID && !isEmbedded" :key="$store.state.user.employeeID" />

        <div v-if="$store.state.showChangePassword" class="error-msg-wrap">
            <div class="error-msg" style="width:auto">
                <div style="font-size:24px">Enter a new password</div>
                <div style="margin-bottom:15px"><input type="password" placeholder="Password" v-model="newPasswordInput" /></div>
                <div @click="() => { $store.state.showChangePassword = false }" class="btn-invert" style="margin-right:10px">Cancel</div>
                <div @click="submitPasswordChange()" class="btn" style="margin-left:10px">Submit</div>
            </div>
        </div>

        
    
        <div v-if="$store.state.user.role == 'public'" style="position:fixed;bottom:10px;left:10px">
            <div class="btn" @click="returnToAdmin()">Return to Admin</div>
        </div>

        <div v-if="$store.state.user.returnAdminID" style="position:fixed;bottom:10px;left:10px">
            <div class="btn" @click="signBackToAdmin()">Return to Admin</div>
        </div>

        <!-- Ironbook staff signed in from the admin portal (API supportSessions.ts) -->
        <div v-if="$store.state.user.supportSession" class="support-bar" role="status">
            <span><b>Support session</b> &middot; {{ $store.state.user.supportSession.by }} as {{ $store.state.user.firstName }} {{ $store.state.user.lastName }} at {{ $store.state.user.companyName }}</span>
            <button type="button" class="btn" @click="endSupportSession()">End Support Session</button>
        </div>

        <!-- Used by accountability chart -->
        <team-hub-role-col v-if="$store.state.showRoleCol" @close="() => { $store.state.showRoleCol = false }" :slug="$store.state.RoleSheetSlug" :colID="$store.state.RoleSheetColID" />
        
        <team-hub-role-col v-if="showTeamHubRoleCol" @close="() => { showTeamHubRoleCol = false }" :slug="$store.state.user.teamHubEmployee.RoleSheetSlug" :colID="$store.state.user.teamHubEmployee.RoleSheetColID" />
        <team-hub-sheet v-if="showTeamHubSheet" :id="teamHubSheetID" @close="() => { showTeamHubSheet = false }" />
        <pdf-pop-up v-if="showPopUp" :url="popUpLink" @close="() => showPopUp = false" />
        <pdf-pop-up v-if="$store.state.showTeamHubChecklist" :url="`/playbook/${$store.state.selectedTeamHubChecklist}?hideNav=t&hideComponent=t&hideChat=t&fullChecklist=t`" @close="() => $store.state.showTeamHubChecklist = false" />

        <div v-if="$store.state.showErrorMsg" class="error-msg-wrap">
            <div class="error-msg">
                <div v-html="$store.state.errorMsg"></div>
                <div @click="() => $store.state.showErrorMsg = false" class="btn">OK</div>
            </div>
        </div>
        <!--
        <div class="error-msg-wrap">
            <div class="error-msg">
                <div v-if="$store.state.user.teamHubChecklists && $store.state.user.teamHubChecklists.length" class="my-checklists">
                    <div class="my-checklists-label">My Checklists</div>
                    <div class="my-checklists-list">
                        <div v-for="(c, i) in $store.state.user.teamHubChecklists" :key="i" :title="c.Name" class="my-checklist-link">
                            <svg-icon icon="checklist" />
                            <span>{{ c.Name }}</span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        -->

        <component :is="'style'" v-if="$store.state.user.primaryColor">
            :root {
                --primary-color: {{ $store.state.user.primaryColor }};
            }
        </component>

        <component :is="'style'" v-if="hideNav">
            .top-bar-wrap { display: none !important; }
            .mobile-nav-show-hide { display: none !important; }
            nav.show-nav, nav.hide-nav { display: none !important; }
            #app-main { max-height: 100vh !important; }
            #playbook-back-btn-wrap { display: none !important; }
            .public-nodes-menu-toggle { display: none !important; }
            #main-content { padding-top: 0 !important; }
            .page-bar { display: none !important; }
            .content { height: 100vh !important; }
            .top-search-wrap { display: none !important; }
        </component>

        <component :is="'style'" v-if="hideNavOS">
            .top-bar-wrap { display: none !important; }
            .mobile-nav-show-hide { display: none !important; }
            nav.show-nav, nav.hide-nav { display: none !important; }
            #app-main { max-height: 100vh !important; }
            #playbook-back-btn-wrap { display: none !important; }
            .public-nodes-menu-toggle { display: none !important; }
            #main-content { padding-top: 0 !important; }
            .content { height: 100vh !important; }
            .top-search-wrap { display: none !important; }
        </component>

        <component :is="'style'" v-if="$store.state.signedIn">
            .content { height: calc(100vh - 59px); }
        </component>

        <component :is="'style'" v-if="!showMenu">
            @media(min-width: 1025px) {
                .fixed-submit { left: 70px; }
                .node-admin-component { left: 70px }
            }
        </component>

        <component :is="'style'" v-if="isEmbedded">
            .top-bar-wrap { display: none !important; }
            #app-main { max-height: 100vh; }
            .page-bar { padding-left: 25px !important; padding-right: 25px !important; }
        </component>

        <component :is="'style'" v-if="$route.query.hideComponent">
            .node-admin-component, .rec-rail { display: none !important; }
        </component>

        <!-- hideChatWidget: something that sits where the widget floats is open (EditorDetailsDrawer.vue) -->
        <component :is="'style'" v-if="$route.query.hideChat || $store.state.hideChatWidget">
            #pb-widget-btn, .af-launch { display: none !important; }
        </component>
    </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapState } from 'vuex';
import axios from 'axios';
import PdfPopUp from './components/PDFPopUp.vue';
import TeamHubSheet from './components/TeamHubSheet.vue';
import TeamHubRoleCol from './components/TeamHubRoleCol.vue';
import UserSwitch from './components/UserSwitch.vue';
import UserSubMenu from './components/UserSubMenu.vue';
import { startActivityTracking, whenIdle } from './activity';
import { startSessionGuard } from './sessionGuard';
import { refreshNumbersToEnter } from './myNumbers';
import MyNumbers from './components/MyNumbers.vue';
import TopSearch from './components/TopSearch.vue';
import NavItem from './components/NavItem.vue';
import QuickAddTask from './components/QuickAddTask.vue';
import QuickAddAgendaItem from './components/QuickAddAgendaItem.vue';
import FloatingTimer from './components/FloatingTimer.vue';
import AskForge from './components/AskForge.vue';
import TaskReminders from './components/TaskReminders.vue';
import { settingsLinks } from './settingsNav';

export default defineComponent({
    name: 'AppView',
    components: {
        PdfPopUp, TeamHubSheet, TeamHubRoleCol, UserSwitch, UserSubMenu, TopSearch, NavItem, QuickAddTask, QuickAddAgendaItem, MyNumbers, FloatingTimer, TaskReminders, AskForge
    },
    data() {
        return {
            loadedInit: false,
            showMenu: true,
            menuBeforeAIDock: null as null | boolean, // the menu's state before Improve with AI docked
            hideOverflow: true,
            appHeightOverride: '<style>#app-main{max-height: 100vh!important}</style>',
            allNodesSearchQ: '',
            allNodesType: 'ALL',
            showAllNodesMenu: false,
            newPasswordInput: '',
            addCompanySheet: false,
            isEmbedded: false,
            popUpLink: '',
            showPopUp: false,
            teamHubSheetID: null as any,
            showTeamHubSheet: false,
            showTeamHubRoleCol: false,
            hideNav: false,
            hideNavOS: false
        }
    },
    async created() {
        // Tracks whether this tab is in use, for pages that refresh after being left alone (activity.ts)
        startActivityTracking();
        // The header's Numbers button: numbers come due weekly, so a tab left open checks again
        whenIdle(15 * 60 * 1000, () => refreshNumbersToEnter());
        // Back to sign-in if this tab's session is lost (401s, and a check when a tab's been left alone)
        startSessionGuard();
        await this.$router.isReady();

        if(this.$route.query.hideNav) {
            this.hideNav = true;
        }
        if(this.$route.query.hideNavOS) {
            this.hideNavOS = true;
        }

        if(typeof window !== 'undefined' && window.self !== window.top) {
            this.isEmbedded = true;
        }

        axios.defaults.withCredentials = true;
        window.alert = (message) => {
            this.$store.state.errorMsg = message;
            this.$store.state.showErrorMsg = true;
        }
        if(window.innerWidth < 992) {
            this.showMenu = false;
            this.$store.state.isMobile = true;
        }

        (document as any).addEventListener("wheel", () => {
            if((document as any).activeElement.type === "number"){
                (document as any).activeElement.blur();
            }
        });

        /*document.onclick = e => {
            if(window.innerWidth < 992) {
                const a = (e.target as any).closest('a');
                if(a && a.href && a.href.includes(window.location.origin)) {
                    window.open(a.href, '_self');
                    return false;
                }
            }
        };*/

        // Edit LogIn.vue and AdminLogIn.vue
        let resetQuery = '';
        if(this.$route.query.reset) {
            resetQuery = '?reset=' + this.$route.query.reset;
        }
        let companySwitchQuery = '';
        if(this.$route.query.companyID) {
            companySwitchQuery = '?companyID=' + this.$route.query.companyID;
        }
        // A support link from the admin portal (API supportSessions.ts): single use, so it comes off the URL once used
        let supportQuery = '';
        if(this.$route.query.support) {
            supportQuery = (resetQuery || companySwitchQuery ? '&' : '?') + 'support=' + encodeURIComponent('' + this.$route.query.support);
        }
        // JWT restores the session if iOS drops the cookie, edit LogIn.vue and AdminLogIn.vue as well
        let jwtQuery = '';
        const jwt = localStorage.getItem('playbook_jwt');
        if(jwt) {
            jwtQuery = (resetQuery || companySwitchQuery || supportQuery ? '&' : '?') + 'jwt=' + encodeURIComponent(jwt);
        }
        await axios.get(`${this.$store.state.apiUrl}/api/session${resetQuery}${companySwitchQuery}${supportQuery}${jwtQuery}`)
            .then(resp => {
                console.log(resp.data);
                // None for a support session
                if(resp.data.jwt) {
                    localStorage.setItem('playbook_jwt', resp.data.jwt);
                } else {
                    localStorage.removeItem('playbook_jwt');
                }
                if(this.$route.query.support) {
                    const query = { ...this.$route.query };
                    delete query.support;
                    this.$router.replace({ query });
                }
                this.$store.state.user = resp.data;
                this.$store.state.signedIn = true;
                if(this.$route.query.public) {
                    this.$store.state.user.role = 'public';
                    this.$store.state.user.isAdmin = false;
                }
                this.$store.dispatch('setStoreData');
                if(this.$route.query.reset) {
                    this.$store.state.showChangePassword = true;
                }
                this.openQuickAddFromQuery();
                localStorage.setItem('admin_refresh_userID', '' + this.$store.state.user.userID);
                setInterval(() => {
                    const storageID = localStorage.getItem('admin_refresh_userID');
                    if(storageID && storageID != ('' + this.$store.state.user.userID)) {
                        window.location.href = '/';
                    }
                }, 5000);
            })
            .catch(e => {
                localStorage.removeItem('playbook_jwt');
                if(this.$route.query.support) {
                    alert(typeof e.response?.data == 'string' && e.response.data ? e.response.data : 'This support link didn\'t work. Start a new one from the admin portal.');
                }
                // Keeps a share link's ?share= (Playbook.vue), so an unpublished page still opens after signing in,
                // and ?quickAdd= (openQuickAddFromQuery), so its popup still opens
                let redirectPath = this.$route.fullPath.split('?')[0];
                const keep = [ 'share', 'quickAdd' ].filter(key => this.$route.query[key])
                    .map(key => key + '=' + encodeURIComponent('' + this.$route.query[key]));
                if(keep.length) {
                    redirectPath += '?' + keep.join('&');
                }
                if(!redirectPath.includes('login')) {
                    this.$router.push('/login?redirect=' + encodeURIComponent(redirectPath));
                } else {
                    this.$router.push('/login');
                }
            })

        setTimeout(() => {
            this.loadedInit = true;
        }, 200);
    },
    methods: {
        // ?quickAdd=task, agenda or numbers on any page opens that header popup, as its button does (for links and
        // shortcuts from outside the app). Needs an employee record, like the buttons. It comes off the URL once
        // read, so a reload or Back doesn't open it again.
        openQuickAddFromQuery() {
            const kind = this.$route.query.quickAdd;
            if(!kind || !this.$store.state.signedIn) {
                return;
            }
            if([ 'task', 'agenda', 'numbers' ].includes(String(kind)) && this.$store.state.user.employeeID) {
                this.$store.state.quickAdd = String(kind);
            }
            const query = { ...this.$route.query };
            delete query.quickAdd;
            this.$router.replace({ query });
        },
        // A quick add's "View" link. The board and meeting pages only open a card from the link when
        // they first load, so a link to the page you're already on reloads it.
        openQuickAddLink() {
            const to = this.$store.state.quickAddToast?.link?.to;
            this.$store.state.quickAddToast = null;
            if(!to) {
                return;
            }
            if(to.split('?')[0] == this.$route.path) {
                window.location.href = to;
            } else {
                this.$router.push(to);
            }
        },
        toggleMenu() {
            this.showMenu = !this.showMenu;
        },
        hideMenuMobile() {
            if(window.innerWidth < 992) {
                this.showMenu = false;
            }
        },
        async logOut(reload = true) {
            localStorage.removeItem('playbook_jwt');
            await axios.get(`${this.$store.state.apiUrl}/api/logout`)
                .catch(e => {
                    console.log(e);
                })

            if(reload) {
                window.location.reload();
            }
        },
        // Records the end in the support log (API /logout), then closes the tab the admin portal opened
        async endSupportSession() {
            await this.logOut(false);
            window.close();
            window.location.href = '/login';
        },
        viewAsPublic() {
            const url = window.location.href.split('?')[0] + '?public=true';
            window.open(url, "_blank");
        },
        handleNode(node: any) {
            if(node.Type == 'LINK') {
                window.open(node.Link, '_blank');
                return;
            }
            this.allNodesSearchQ = '';
            this.allNodesSearch();
            this.$router.push('/playbook/' + node.Slug);
        },
        allNodesSearch() {
            let tempAllNodesFiltered = [];
            if(this.allNodesSearchQ.length < 3) {
                tempAllNodesFiltered = [];
            } else {
                const searchQLower = this.allNodesSearchQ.toLowerCase();
                tempAllNodesFiltered = this.$store.state.allNodes.filter((node: any) => {
                    return node.Name.toLowerCase().includes(searchQLower);
                })
            }
            this.$store.state.allNodesFiltered = tempAllNodesFiltered;
            if(this.$store.state.allNodesFiltered.length) {
                this.showAllNodesMenu = true;
            } else {
                this.showAllNodesMenu = false;
            }
        },
        returnToAdmin() {
            window.location.href = window.location.href.split('?')[0];
        },
        signBackToAdmin() {
            axios.get(`${this.$store.state.apiUrl}/api/returnadmin`)
                .then(resp => {
                    window.location.href = '/';
                })
                .catch(e => {
                    console.log(e);
                    alert('There was an error')
                })
        },
        async submitPasswordChange() {
            if(!this.newPasswordInput) {
                return;
            }
            this.newPasswordInput = this.newPasswordInput.trim();
            const data = {
                password: this.newPasswordInput
            }
            const endpoint = this.$store.state.user.isAdmin ? 'changepassword' : 'changepublicpassword';
            await axios.post(`${this.$store.state.apiUrl}/api/${endpoint}`, data)
                .then(resp => {
                    this.$store.state.showChangePassword = false;
                })
                .catch(e => {
                    alert('There was an error')
                })
        },
        switchCompany() {
            window.location.href = window.location.origin + `?companyID=` + this.$store.state.user.companyID;
        },
        openPopUp(l: string) {
            let link = l;
            if(link.includes('/playbook/')) {
                link = link.replace('https://', '').replace('http://', '');
                link = link.split('?')[0];
                const firstSlashIndex = link.indexOf('/');
                link = link.substring(firstSlashIndex, link.length) + '?hideNav=t&hideComponent=t&hideChat=t';
            }
            this.popUpLink = link;
            this.showPopUp = true;
        },
        toggleFavorite(key: string) {
            this.$store.dispatch('toggleFavorite', key);
        },
        showTeamHubSheetFn(tableID: number) {
            this.teamHubSheetID = tableID;
            this.showTeamHubSheet = true;
        }
    },
    computed: {
        ...mapState(['routeLoaded']),
        // Nav menu sections. Entries are either { subhead } or an item:
        // { key, label, icon, to? | href? | action?, count?, aiTag?, class? }
        // key identifies the item in the user's favorites, so keep keys stable.
        // Same people who get The Forge in the nav
        canForge(): boolean {
            const user = this.$store.state.user;
            return !!(user && user.isAdmin && ['Admin', 'Editor'].includes(user.role));
        },
        navSections(): any[] {
            const user = this.$store.state.user;
            const route = (to: string, label: string, icon: string, extra: any = {}) => ({ key: to, to, label, icon, ...extra });
            const action = (key: string, label: string, icon: string, fn: () => void) => ({ key: 'action:' + key, label, icon, action: fn });
            const sections: any[] = [];

            const playbook: any[] = [route('/playbook/home', 'Home', 'lines')];
            if(user.isAdmin && ['Admin', 'Editor'].includes(user.role)) {
                playbook.push(route('/ai-hub', 'The Forge', 'flame', { class: 'ai-nav' }));
            }

            const employee = user.teamHubEmployee;

            let deckName = 'Decks';
            if(this.$store.state.user.companyID == 1) {
                deckName = 'Decks (Trello)'
            }

            if(!user.isAdmin) {
                playbook.push({ subhead: 'MY WORK' });
                let checklists = user.teamHubChecklists || [];
                if(checklists.length == 1) {
                    const c = user.teamHubChecklists[0];
                    playbook.push({ key: 'checklist:' + c.Slug, label: `Today's Powerlist`, icon: 'checklist', action: () => {
                        this.$store.state.selectedTeamHubChecklist = c.Slug;
                        this.$store.state.showTeamHubChecklist = true;
                    }});
                } else if(checklists.length > 1) {
                    for(const c of user.teamHubChecklists || []) {
                        playbook.push({ key: 'checklist:' + c.Slug, label: c.Name, icon: 'checklist', action: () => {
                            this.$store.state.selectedTeamHubChecklist = c.Slug;
                            this.$store.state.showTeamHubChecklist = true;
                        }});
                    }
                }
                if(employee?.RoleSheetSlug && employee?.RoleSheetColID) {
                    playbook.push(action('my-role', 'My Role', 'twousers', () => { this.showTeamHubRoleCol = true; }));
                }
                if(employee) {
                    playbook.push(route('/employee/' + employee.ID, 'My Record', 'user'));
                }
                playbook.push(route('/decks', deckName, 'verticalbars'));
                playbook.push(route('/meetings', 'Meetings', 'classroom'));
                playbook.push(route('/classrooms', 'Training', 'classroom'));
            }

            // Marks where the user's favorites are listed: right after Home / AI Assistant
            playbook.push({ favorites: true });

            playbook.push({ subhead: 'FOUNDATIONS' });
            playbook.push(route('/nodes/PROCESS', 'Processes', 'papers'));
            if(user.isAdmin) {
                playbook.push(route('/nodes/CHECKLIST', 'Powerlists', 'checklist'));
            }
            playbook.push(route('/nodes/SHEET', 'Role Sheets', 'table'));
            playbook.push(route('/nodes/TIMELINE', 'Value Engines', 'timeline'));
            // Team Hub Admins see every team's scoreboard, everyone else their own team's (company rocks are for everyone)
            if(user.teamHubRole == 'Admin' || employee) {
                playbook.push(route('/scoreboard', 'Scoreboard', 'chart'));
            }
            
            if(this.$store.state.user.companyID == 3) {
                playbook.push(route('/company-sheets', 'Spreadsheets', 'table'));
            }

            if(user.isAdmin) {
                playbook.push(route('/review', 'Review', 'clipboard', {
                    count: this.$store.state.nodeCounts.needsReview,
                    class: this.$route.path == '/review' ? 'router-link-exact-active' : ''
                }));
            } else {
                // playbook.push(route('/nodes/ALL', 'Process Sign Offs', 'papers'));
            }

            if(user.isAdmin) {
                playbook.push({ subhead: 'WORK' });
                playbook.push(route('/decks', deckName, 'verticalbars'));
                playbook.push(route('/meetings', 'Meetings', 'classroom'));
                playbook.push(route('/classrooms', 'Training', 'classroom'));
                playbook.push(route('/forms', 'Forms', 'papers'));
            }

            playbook.push({ subhead: 'TEAM' });
            playbook.push(route('/announcements', 'Announcements', 'loudspeaker'));
            
            if(user.teamHubRole == 'Admin' || employee?.IsManager) {
                playbook.push(route('/employees', user.teamHubRole == 'Admin' ? 'Employees' : 'My Employees', 'twousers'));
            }
            playbook.push(route('/accountability-chart', 'Accountability Chart', 'orgchart'));
            playbook.push(route('/skills-matrix', 'Skills Matrix', 'table'));
            playbook.push(route('/celebrations', 'Celebrations', 'party'));
            playbook.push(route('/onboarding', 'Onboarding', 'lines'));

            if(user.teamHubLinks?.length) {
                playbook.push({ subhead: 'DOCS & LINKS' });
                for(const link of user.teamHubLinks || []) {
                    if(link.type == 'newtab') {
                        playbook.push({ key: 'link:newtab:' + link.link, label: link.name, icon: 'link', href: link.link });
                    } else if(link.type == 'embed') {
                        playbook.push({ key: 'link:embed:' + link.link, label: link.name, icon: 'link', action: () => this.openPopUp(link.link) });
                    } else if(link.type == 'table') {
                        playbook.push({ key: 'link:table:' + link.tableID, label: link.name, icon: 'link', action: () => this.showTeamHubSheetFn(link.tableID) });
                    } else if(link.type == 'internal') {
                        playbook.push({ key: 'link:internal:' + link.link, label: link.name, icon: 'link', to: link.link });
                    }
                }
            }

            const settings = settingsLinks(user);
            if(settings.length) {
                playbook.push({ subhead: 'SETTINGS' });
                for(const link of settings) playbook.push(route(link.to, link.label, link.icon));
            }
            sections.push({ name: 'The Book', entries: playbook });

            return sections;
        },
        // Favorites in the order they were added, limited to items the user can currently see
        favoriteItems(): any[] {
            const byKey: any = {};
            for(const section of this.navSections) {
                for(const entry of section.entries) {
                    if(entry.key && !byKey[entry.key]) byKey[entry.key] = entry;
                }
            }
            // Favorited boards ('board:<slug>'); only boards the user can still see resolve
            for(const board of this.$store.state.companyBoards) {
                const key = 'board:' + board.Slug;
                byKey[key] = { key, to: '/deck/' + board.Slug, label: board.Name, icon: 'verticalbars' };
            }
            // Favorited playbook nodes ('node:<slug>'), from the nodes the user can see; links open in a new tab like in Nodes.vue
            const nodeIcons: any = { PROCESS: 'papers', TIMELINE: 'timeline', SHEET: 'table', CHECKLIST: 'checklist', VIDEO: 'play', LINK: 'link' };
            for(const node of this.$store.state.allNodes) {
                const key = 'node:' + node.Slug;
                const target = node.Type == 'LINK' ? { href: node.Link } : { to: '/playbook/' + node.Slug };
                byKey[key] = { key, ...target, label: node.Name, icon: nodeIcons[node.Type] || 'lines' };
            }
            return this.$store.state.navFavorites.map((key: string) => byKey[key]).filter(Boolean);
        }
    },
    watch: {
        // Improve with AI docked on the right (AIImprovePanel.vue): fold the left menu for room, then put it back as it was
        '$store.state.aiDocked'(on: boolean) {
            if (on) { if (this.menuBeforeAIDock === null) this.menuBeforeAIDock = this.showMenu; this.showMenu = false; }
            else if (this.menuBeforeAIDock !== null) { this.showMenu = this.menuBeforeAIDock; this.menuBeforeAIDock = null; }
        },
        // Signing in (or switching user / company): whether there are numbers to enter
        '$store.state.user.employeeID': {
            handler() {
                refreshNumbersToEnter();
            },
            immediate: true
        },
        // A ?quickAdd= link followed while the app is already open
        '$route.query.quickAdd'() {
            this.openQuickAddFromQuery();
        },
        // A quick add's confirmation goes away on its own after a few seconds
        '$store.state.quickAddToast'(toast: any) {
            clearTimeout((this as any).quickAddToastTimer);
            if(toast) {
                (this as any).quickAddToastTimer = setTimeout(() => { this.$store.state.quickAddToast = null; }, 7000);
            }
        },
        $route (to, from){
            if(to.path == from.path) {
                return;
            }
            this.$store.state.showSubMenu = false;
            this.$store.state.routeLoaded = false;
            this.hideMenuMobile();
        },
        routeLoaded(newVal) {
            if(newVal) {
                setTimeout(() => { this.hideOverflow = false; }, 600)
            } else {
                this.hideOverflow = true;
            }
        }
    }
});
</script>

<style lang="scss" src="./style.scss"></style>
